import { useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent, FormEvent } from 'react'
import {
  ApiError,
  UnauthorizedError,
  referenceLibraryApi,
  type CatalogDocumentDTO,
  type ReferenceType,
} from '@/shared/api'
import { Icon } from '@/shared/ui/Icon'
import { BetaUploadNotice } from '@/shared/ui/BetaUploadNotice'
import { ReferenceMetadataForm } from './ReferenceMetadataForm'
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_FILE_LABEL,
  deriveDocumentIdFromFilename,
  formatBytes,
  isValidReferenceYear,
} from './referenceUtils'
// Step 13.7-FIX — shared catalog dup-check keyed on tenant + document_id, plus
// the matched-row title helper, reused so the warning can name the conflict.
import {
  documentTitle,
  findDuplicateDocument,
} from '@/features/documents/documentUtils'
import type {
  ReferenceUploadFormErrors,
  ReferenceUploadFormValues,
  ReferenceUploadSuccessSummary,
} from './types'

interface Props {
  tenantId: string
  organizationLabel: string
  existingDocuments: CatalogDocumentDTO[]
  onUploaded: (summary: ReferenceUploadSuccessSummary) => void
}

const INITIAL_VALUES: ReferenceUploadFormValues = {
  file: null,
  documentId: '',
  title: '',
  referenceType: '',
  referenceDomain: '',
  referenceYear: '',
  referenceSource: '',
}

function validate(values: ReferenceUploadFormValues): ReferenceUploadFormErrors {
  const errors: ReferenceUploadFormErrors = {}
  if (!values.file) errors.file = 'Selecione um arquivo.'
  if (!isValidReferenceYear(values.referenceYear)) {
    errors.referenceYear = 'Use 4 dígitos entre 1900 e 2100.'
  }
  return errors
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Reference Library upload form.
 *
 * Differences from the Documents Library upload:
 *   - No entity/group fields (reference docs are reusable, not patient-scoped).
 *   - reference_type is a closed list; reference_year is a 4-digit number.
 *   - Duplicate check uses ``(tenantId, document_id)`` only — the simpler
 *     functional key for reference docs (no entity/group).
 *
 * Auth: same cookie-only client (``credentials: 'include'``); no tokens.
 */
export function ReferenceUploadPanel({
  tenantId,
  organizationLabel,
  existingDocuments,
  onUploaded,
}: Props) {
  const [values, setValues] = useState<ReferenceUploadFormValues>(INITIAL_VALUES)
  const [errors, setErrors] = useState<ReferenceUploadFormErrors>({})
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState<ReferenceUploadSuccessSummary | null>(
    null,
  )
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [documentIdTouched, setDocumentIdTouched] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function updateField(
    field: keyof ReferenceUploadFormValues,
    value: string,
  ) {
    setValues((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: undefined, form: undefined }))
    setSuccess(null)
    if (field === 'documentId') {
      setDocumentIdTouched(value.trim().length > 0)
      setConfirmReplace(false)
    }
  }

  function handleFile(file: File | null | undefined) {
    if (!file) return
    setValues((prev) => {
      const next: ReferenceUploadFormValues = { ...prev, file }
      if (!documentIdTouched) {
        next.documentId = deriveDocumentIdFromFilename(file.name)
      }
      return next
    })
    setErrors((prev) => ({ ...prev, file: undefined, form: undefined }))
    setSuccess(null)
    setConfirmReplace(false)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  function resetForm() {
    setValues(INITIAL_VALUES)
    setErrors({})
    setDocumentIdTouched(false)
    setConfirmReplace(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const effectiveDocumentId = useMemo(() => {
    const typed = values.documentId.trim()
    if (typed) return typed
    return values.file ? deriveDocumentIdFromFilename(values.file.name) : ''
  }, [values.documentId, values.file])

  const duplicateDocument = useMemo(
    () => findDuplicateDocument(existingDocuments, tenantId, effectiveDocumentId),
    [existingDocuments, tenantId, effectiveDocumentId],
  )
  const isDuplicate = duplicateDocument !== null

  useEffect(() => {
    if (!isDuplicate && confirmReplace) setConfirmReplace(false)
  }, [isDuplicate, confirmReplace])

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (isSubmitting) return
    setSuccess(null)

    const validation = validate(values)
    if (Object.keys(validation).length > 0) {
      setErrors(validation)
      return
    }
    if (isDuplicate && !confirmReplace) {
      setErrors({
        form:
          'Já existe uma referência com esse ID. Marque "Substituir referência existente" para reindexar, ou edite o ID.',
      })
      return
    }

    const file = values.file!
    const documentId =
      values.documentId.trim() || deriveDocumentIdFromFilename(file.name)
    const referenceYear = values.referenceYear.trim()
      ? Number(values.referenceYear.trim())
      : null

    setIsSubmitting(true)
    setErrors({})

    try {
      const response = await referenceLibraryApi.upload({
        file,
        tenantId,
        documentId,
        referenceType:
          (values.referenceType as ReferenceType) || null,
        referenceDomain: values.referenceDomain.trim() || null,
        referenceYear,
        referenceSource: values.referenceSource.trim() || null,
        title: values.title.trim() || null,
      })
      const summary: ReferenceUploadSuccessSummary = {
        document_id: response.document_id,
        filename: response.filename,
        chunks_indexed: response.chunks_indexed,
        replaced: isDuplicate,
        status: response.status === 'queued' ? 'queued' : 'indexed',
        job_id: response.job_id ?? null,
      }
      setSuccess(summary)
      onUploaded(summary)
      resetForm()
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setErrors({ form: 'Sessão expirada. Faça login novamente.' })
      } else {
        setErrors({
          form: errorDetail(err, 'Falha ao enviar referência.'),
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const file = values.file
  const submitDisabled =
    isSubmitting || !file || (isDuplicate && !confirmReplace)
  const submitLabel = isSubmitting
    ? 'Enviando…'
    : isDuplicate
      ? 'Substituir e reindexar'
      : 'Indexar referência'

  return (
    <section className="doc-upload-panel" aria-labelledby="ref-upload-title">
      <header className="doc-upload-head">
        <h2 id="ref-upload-title">Adicionar referência</h2>
        <p className="doc-upload-sub">
          Workspace: <strong>{organizationLabel}</strong>
          <span className="doc-upload-tenant">
            {' '}
            · tenant <code>{tenantId}</code>
          </span>
        </p>
      </header>

      <BetaUploadNotice />

      <form
        className="doc-upload-form"
        onSubmit={(e) => void handleSubmit(e)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          const target = e.target as HTMLElement
          if (
            target instanceof HTMLInputElement &&
            target.type !== 'submit' &&
            target.type !== 'checkbox' &&
            target.type !== 'file'
          ) {
            e.preventDefault()
          }
        }}
        noValidate
      >
        <div
          className={`doc-dropzone${isDragging ? ' is-dragging' : ''}${
            errors.file ? ' has-error' : ''
          }${isSubmitting ? ' is-uploading' : ''}`}
          onClick={() => !isSubmitting && fileInputRef.current?.click()}
          onDragOver={(e) => {
            if (isSubmitting) return
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => !isSubmitting && handleDrop(e)}
          role="button"
          tabIndex={isSubmitting ? -1 : 0}
          aria-disabled={isSubmitting}
          onKeyDown={(e) => {
            if (isSubmitting) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              fileInputRef.current?.click()
            }
          }}
        >
          <div className="doc-dropzone-icon" aria-hidden="true">
            <Icon name="paperclip" />
          </div>
          {file ? (
            <div className="doc-dropzone-file">
              <div className="doc-dropzone-filename">{file.name}</div>
              <div className="doc-dropzone-meta">
                {formatBytes(file.size)} ·{' '}
                {isSubmitting ? 'enviando…' : 'clique para trocar'}
              </div>
            </div>
          ) : (
            <div className="doc-dropzone-prompt">
              <strong>Arraste e solte o arquivo aqui</strong>
              <span>
                ou clique para selecionar — {ACCEPTED_FILE_LABEL}
              </span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            data-testid="reference-upload-file-input"
            accept={ACCEPTED_FILE_EXTENSIONS}
            className="doc-dropzone-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={isSubmitting}
          />
          {isSubmitting ? (
            <div className="doc-dropzone-progress" aria-hidden="true">
              <div className="doc-dropzone-progress-bar" />
            </div>
          ) : null}
        </div>
        {errors.file ? (
          <p className="doc-field-error" role="alert">
            {errors.file}
          </p>
        ) : null}

        <ReferenceMetadataForm
          values={values}
          errors={errors}
          disabled={isSubmitting}
          onChange={updateField}
        />

        {duplicateDocument ? (
          <div className="doc-upload-alert is-warning" role="alert">
            <Icon name="sparkles" />
            <div>
              <strong>Este document_id já existe neste workspace.</strong>
              <p>
                O ID <code>{effectiveDocumentId}</code> corresponde a{' '}
                <strong>{documentTitle(duplicateDocument)}</strong>
                {duplicateDocument.filename &&
                documentTitle(duplicateDocument) !== duplicateDocument.filename
                  ? ` (${duplicateDocument.filename})`
                  : ''}
                . Se continuar com substituição ativada, a referência existente
                será <strong>reindexada</strong>. Altere o document_id para criar
                um novo registro.
              </p>
              <label className="doc-upload-confirm">
                <input
                  type="checkbox"
                  checked={confirmReplace}
                  onChange={(e) => setConfirmReplace(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span>Substituir referência existente</span>
              </label>
            </div>
          </div>
        ) : null}

        {errors.form ? (
          <div className="doc-upload-alert is-error" role="alert">
            <Icon name="close" /> {errors.form}
          </div>
        ) : null}

        {success ? (
          <div className="doc-upload-alert is-success" role="status" data-testid="reference-upload-success">
            <Icon name="sparkles" />
            {success.status === 'queued' ? (
              <span>
                <strong>{success.filename}</strong> enviado · na fila para
                processamento · id <code>{success.document_id}</code>. Acompanhe
                em “Ingestões recentes”.
              </span>
            ) : (
              <span>
                <strong>{success.filename}</strong>{' '}
                {success.replaced ? 'reindexado' : 'indexado'} ·{' '}
                {success.chunks_indexed} chunks · id{' '}
                <code>{success.document_id}</code>
              </span>
            )}
          </div>
        ) : null}

        <div className="doc-upload-actions">
          <button
            type="submit"
            className={`doc-upload-submit${isDuplicate ? ' is-replace' : ''}`}
            data-testid="reference-upload-submit"
            disabled={submitDisabled}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </section>
  )
}
